import React from 'react';
import { Filter, isNullOperator, rawExpressionToFilter } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box, Button } from '@mui/material';
import { FieldFilterModeType, isAdvancedFilterMode } from '@utils/filterUtils';
import { AdvancedFilterValue, ConditionOperand, FieldFilterValue, LogicalOperatorType } from '../types';

interface FilterActionButtonsProps {
  setErrors: React.Dispatch<React.SetStateAction<ConditionOperand[]>>;
  setConditionValue: React.Dispatch<React.SetStateAction<FieldFilterValue>>;
  operator: LogicalOperatorType;
  initialFilter: ConditionOperand;
  setOpen: (open: boolean) => void;
  onChange: (field: string, value: unknown) => void;
  field: string;
  onFilterChange: ((filter: Filter) => void) | undefined;
  conditionValue: FieldFilterValue;
  getErrors: (val: unknown, operator: string | undefined) => string[] | undefined;
  mode: FieldFilterModeType;
  advancedFilterValue: AdvancedFilterValue;
  setAdvancedFilterValue: React.Dispatch<React.SetStateAction<AdvancedFilterValue>>;
}

function FilterActionButtons({
  setErrors,
  setConditionValue,
  operator,
  initialFilter,
  setOpen,
  onChange,
  field,
  onFilterChange,
  conditionValue,
  getErrors,
  mode,
  advancedFilterValue,
  setAdvancedFilterValue,
}: FilterActionButtonsProps) {
  const isAdvancedFilterComplete = React.useMemo(() => {
    if (!isAdvancedFilterMode(mode)) {
      return true; // Not in advanced mode, so this check doesn't apply
    }
    const { dir, top, value } = advancedFilterValue;
    return dir !== undefined && top !== undefined && top !== null && value !== undefined;
  }, [mode, advancedFilterValue]);

  const onApply = () => {
    if (isAdvancedFilterMode(mode)) {
      onChange(field, [encodeURIComponent(JSON.stringify(advancedFilterValue))]);
      onFilterChange?.(rawExpressionToFilter(field, advancedFilterValue));
      setOpen(false);
    } else {
      const validOperands = conditionValue[operator]!.filter(
        ({ value, operator }: ConditionOperand) => typeof value === 'number' || isNullOperator(operator) || !!value
      );

      if (!validOperands.length) {
        setConditionValue({ [operator]: [initialFilter] });
        onChange(field, []);
        onFilterChange?.(rawExpressionToFilter(field, conditionValue));
        setOpen(false);
      } else {
        const errorsExp = validOperands.map(({ value, operator }: ConditionOperand) => ({
          operator,
          value: getErrors(value, operator),
        }));
        if (errorsExp.some(({ value }) => value)) {
          setErrors(errorsExp);
        } else {
          const nextConditionValue = { [operator]: validOperands };
          setConditionValue(nextConditionValue);
          onChange(field, [encodeURIComponent(JSON.stringify(nextConditionValue))]);
          onFilterChange?.(rawExpressionToFilter(field, nextConditionValue));
          setOpen(false);
        }
      }
    }
  };

  return (
    <Box sx={{ p: 2, ...flex.justifyBetweenCenter }}>
      <Button
        onClick={() => {
          if (isAdvancedFilterMode(mode)) {
            // In advanced mode `initialFilter` is the empty `{}` placeholder (see useFieldFilterState),
            // which satisfies AdvancedFilterValue's all-optional shape even though the static prop type
            // is ConditionOperand (its shape when used for Condition mode resets).
            setAdvancedFilterValue(initialFilter as AdvancedFilterValue);
          }
          setErrors([]);
          setConditionValue({ [operator]: [initialFilter] });
        }}
        size="small">
        Reset
      </Button>
      <div>
        <Button onClick={() => setOpen(false)} size="small">
          Close
        </Button>
        <Button variant="contained" size="small" disabled={isAdvancedFilterMode(mode) && !isAdvancedFilterComplete} onClick={onApply}>
          Apply
        </Button>
      </div>
    </Box>
  );
}

export default FilterActionButtons;
